import pluralize from "pluralize";

type ProgressClause = {
	verb: string;
	clause: string;
};

type TopicSummary = {
	text: string;
	coversAll: boolean;
};

type SessionSummaryInput = {
	progresses: string[];
	itemCount: number;
	modeLabel: string;
	selectedTopics: string[];
	totalTopicCount: number;
	timeEstimate?: string;
};

const VERB_BY_PROGRESS: Record<string, string> = {
	none: "Study",
	low: "Review",
	medium: "Practice",
	high: "Refresh",
};

const DESC_BY_PROGRESS: Record<string, string> = {
	none: "unanswered",
	low: "needs work",
	medium: "almost there",
	high: "mastered",
};

const normalizeProgressKey = (progresses: string[]): string => {
	const uniq = Array.from(new Set(progresses)).sort();
	if (uniq.length === 0) {
		return "noneSelected";
	}
	if (uniq.length === 4) {
		return "all";
	}
	return uniq.join("+");
};

const buildProgressClause = (progressKey: string): ProgressClause => {
	switch (progressKey) {
		case "none":
			return {
				verb: VERB_BY_PROGRESS.none,
				clause: ` that are ${DESC_BY_PROGRESS.none}`,
			};
		case "low":
			return {
				verb: VERB_BY_PROGRESS.low,
				clause: ` that ${DESC_BY_PROGRESS.low}`,
			};
		case "medium":
			return {
				verb: VERB_BY_PROGRESS.medium,
				clause: ` that are ${DESC_BY_PROGRESS.medium}`,
			};
		case "high":
			return {
				verb: VERB_BY_PROGRESS.high,
				clause: ` that are ${DESC_BY_PROGRESS.high}`,
			};
		case "none+low":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none} or ${DESC_BY_PROGRESS.low}`,
			};
		case "low+medium":
			return {
				verb: "Work on",
				clause: ` that ${DESC_BY_PROGRESS.low} or are ${DESC_BY_PROGRESS.medium}`,
			};
		case "medium+high":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.medium} or ${DESC_BY_PROGRESS.high}`,
			};
		case "none+medium":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none} or ${DESC_BY_PROGRESS.medium}`,
			};
		case "none+high":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none} or ${DESC_BY_PROGRESS.high}`,
			};
		case "low+high":
			return {
				verb: "Work on",
				clause: ` that ${DESC_BY_PROGRESS.low} or are ${DESC_BY_PROGRESS.high}`,
			};
		case "none+low+medium":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none}, ${DESC_BY_PROGRESS.low}, or ${DESC_BY_PROGRESS.medium}`,
			};
		case "low+medium+high":
			return {
				verb: "Work on",
				clause: ` that ${DESC_BY_PROGRESS.low}, are ${DESC_BY_PROGRESS.medium}, or ${DESC_BY_PROGRESS.high}`,
			};
		case "none+medium+high":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none}, ${DESC_BY_PROGRESS.medium}, or ${DESC_BY_PROGRESS.high}`,
			};
		case "none+low+high":
			return {
				verb: "Work on",
				clause: ` that are ${DESC_BY_PROGRESS.none}, ${DESC_BY_PROGRESS.low}, or ${DESC_BY_PROGRESS.high}`,
			};
		case "all":
			return { verb: "Study", clause: " from all mastery levels" };
		case "noneSelected":
			return { verb: "Study", clause: "" };
		default: {
			const parts = progressKey.split("+").map((k) => k.trim());
			const human = parts.map((k) =>
				k === "low" ? "needs work" : DESC_BY_PROGRESS[k] || k,
			);
			const left = human.slice(0, -1).join(", ");
			const last = human[human.length - 1];
			const joined =
				human.length > 2 ? `${left}, or ${last}` : human.join(" or ");
			return {
				verb: "Work on",
				clause: ` that ${joined.startsWith("need") ? joined : `are ${joined}`}`,
			};
		}
	}
};

const summarizeTopics = (
	selected: string[],
	totalCount: number,
): TopicSummary => {
	const n = selected.length;
	if (n === 0 || n === totalCount) {
		return {
			text: `all ${totalCount} ${pluralize("topic", totalCount)}`,
			coversAll: true,
		};
	}
	if (n === 1) {
		return { text: selected[0], coversAll: false };
	}
	if (n === 2) {
		return { text: `${selected[0]} and ${selected[1]}`, coversAll: false };
	}
	return {
		text: `${selected[0]}, ${selected[1]}, and ${n - 2} more`,
		coversAll: false,
	};
};

const maybeAdjustVerbForTopics = (verb: string, coversAll: boolean): string => {
	if (coversAll && verb === "Work on") {
		return "Study";
	}
	return verb;
};

export const getSessionSummary = ({
	progresses,
	itemCount,
	modeLabel,
	selectedTopics,
	totalTopicCount,
	timeEstimate,
}: SessionSummaryInput): string => {
	const progressKey = normalizeProgressKey(progresses);
	let { verb, clause } = buildProgressClause(progressKey);

	const { text: topicText, coversAll } = summarizeTopics(
		selectedTopics,
		totalTopicCount,
	);
	verb = maybeAdjustVerbForTopics(verb, coversAll);

	const timeText = timeEstimate ? ` (about ${timeEstimate})` : "";
	const topicPhrase = topicText
		? coversAll
			? ` across ${topicText}`
			: ` from ${topicText}`
		: "";

	return `${verb} ${itemCount} ${pluralize(modeLabel, itemCount)}${topicPhrase}${clause}${timeText}.`;
};
